import type { TableColumn } from '@/_shared/components/Table/types';
import { parseNumeric, rowValue } from '@/_shared/components/Table/utils';
import { coerceLabel } from '@/_shared/lib/coerce-label';

import { isTimestampColumn } from './timestamp';

type Row = Record<string, unknown>;

function isNumericColumn(rows: Row[], id: string): boolean {
  let seen = 0;
  for (const row of rows) {
    const value = rowValue(row, id);
    if (coerceLabel(value).trim() === '') continue;
    if (Number.isNaN(parseNumeric(value))) return false;
    seen += 1;
  }
  return seen > 0;
}

/**
 * Build columns for a set of dynamic rows whose shape isn't known at compile
 * time (warehouse query results, assistant table payloads). The sort strategy is
 * chosen by inspecting the column's values, not assumed:
 *
 * - all-timestamp column → chronological sort, value shown verbatim (SQL formatting respected);
 * - all-numeric column → numeric sort;
 * - anything else → the default alphabetical sort.
 *
 * Alphabetical (free-text) columns also get `wrap: true` — same convention every hand-authored
 * column list in the app already follows (`dashboardColumns`, `workflowColumns`, `contextColumns`,
 * `AudienceListTable`, …) for description-like text. Without it, a long value (e.g. a comma-joined
 * column list) renders `whitespace-nowrap` and the table's own `overflow-auto` scroll container
 * becomes the only way to read the rest — invisible and undiscoverable in a narrow host (the
 * audience artifact page's chat pane is capped at `md:w-[26rem]`), so it reads as hard-clipped.
 * Numeric/timestamp values are short and single-token, so they stay `nowrap` for dense scanning.
 */
export function deriveColumns(rows: Row[], columnOrder?: string[]): TableColumn<Row>[] {
  const ids = columnOrder ?? (rows[0] ? Object.keys(rows[0]) : []);
  return ids.map((id) => {
    if (isTimestampColumn(rows, id)) {
      // Chronological sort; the value renders verbatim via the Table's default cell (coerceLabel),
      // so the SQL author's date formatting is respected (ACT2-401).
      return { id, header: id, sorter: 'timestamp' };
    }
    if (isNumericColumn(rows, id)) {
      return { id, header: id, sorter: 'numeric' };
    }
    return { id, header: id, sorter: 'alphabetical', wrap: true };
  });
}
